/**
 * City Network Data
 *
 * Scalable city-based cigar culture guide system.
 * Ybor City = Node #1 (prototype for expansion)
 */

export interface CityLounge {
  id: string;
  name: string;
  address: string;
  neighborhood?: string;
  phone?: string;
  website?: string;
  hours?: string;
  description: string;
  amenities: string[];
  specialties?: string[]; // Featured cigars or house blends
  atmosphere: 'casual' | 'upscale' | 'historic' | 'modern' | 'neighborhood';
  priceRange: '$' | '$$' | '$$$';
  coordinates?: { lat: number; lng: number };
  featured?: boolean;
  imageUrl?: string;
}

export interface LocalBrand {
  id: string;
  name: string;
  founded?: string;
  story: string;
  notableCigars: string[];
  whereToFind: string[];
  website?: string;
  featured?: boolean;
}

export interface CityEvent {
  id: string;
  name: string;
  date?: string; // Can be specific date or recurring description
  venue?: string;
  description: string;
  type: 'festival' | 'tasting' | 'release' | 'meetup' | 'recurring';
  link?: string;
}

export interface HistoricalNote {
  id: string;
  title: string;
  content: string;
  year?: string;
  category: 'founding' | 'industry' | 'culture' | 'landmark' | 'notable-figure';
}

export interface CityGuide {
  id: string;
  name: string;
  tagline: string;
  region: string;
  country: string;
  heroImage?: string;

  // Overview
  overview: string;
  cigarSignificance: string; // Why this city matters to cigar culture

  // Content sections
  lounges: CityLounge[];
  localBrands: LocalBrand[];
  events: CityEvent[];
  history: HistoricalNote[];

  // Cultural notes
  culturalTips: string[];
  bestTimeToVisit?: string;
  gettingAround?: string;

  // Navigation
  neighborhoodHighlights?: {
    name: string;
    description: string;
    vibe: string;
  }[];

  // Status
  isLive: boolean;
  comingSoon?: boolean;
}

// ============================================
// YBOR CITY - NODE #1
// ============================================

const YBOR_CITY_LOUNGES: CityLounge[] = [
  {
    id: 'king-corona',
    name: 'King Corona Cigars Cafe',
    address: '1523 E 7th Ave, Tampa, FL 33605',
    neighborhood: 'Historic 7th Avenue',
    phone: '(813) 241-9109',
    website: 'https://kingcoronacigars.com',
    hours: 'Mon-Thu 11am-10pm, Fri-Sat 11am-12am, Sun 12pm-8pm',
    description: 'Iconic Ybor City institution since 1997. Full bar, craft cocktails, and one of the best humidors in Tampa. Live music on weekends.',
    amenities: ['Full Bar', 'Food Menu', 'Live Music', 'Outdoor Patio', 'Walk-in Humidor', 'WiFi'],
    specialties: ['House-rolled cigars', 'King Corona Exclusives'],
    atmosphere: 'historic',
    priceRange: '$$',
    coordinates: { lat: 27.9602, lng: -82.4389 },
    featured: true,
  },
  {
    id: 'tabanero',
    name: 'Tabanero Cigars',
    address: '1601 E 7th Ave, Tampa, FL 33605',
    neighborhood: 'Historic 7th Avenue',
    phone: '(813) 248-9109',
    website: 'https://tabanerocigars.com',
    hours: 'Mon-Sat 10am-10pm, Sun 12pm-6pm',
    description: 'Watch master rollers craft cigars by hand in this working factory and lounge. Award-winning house blends and Cuban-style cafe.',
    amenities: ['Cigar Rolling Demonstrations', 'Cuban Coffee', 'Factory Tours', 'Retail Shop'],
    specialties: ['Tabanero Red Eye', 'Tabanero Havana'],
    atmosphere: 'historic',
    priceRange: '$$',
    coordinates: { lat: 27.9604, lng: -82.4375 },
    featured: true,
  },
  {
    id: 'tampa-sweethearts',
    name: 'Tampa Sweethearts Cigar Co.',
    address: '1713 E 7th Ave, Tampa, FL 33605',
    neighborhood: 'Historic 7th Avenue',
    phone: '(813) 247-2469',
    website: 'https://tampasweethearts.com',
    hours: 'Mon-Sat 10am-6pm',
    description: 'Family-owned since 1920. One of the last original cigar factories in Ybor. Hand-rolled cigars using traditional Cuban methods.',
    amenities: ['Factory Tours', 'Hand-rolling', 'Historic Building'],
    specialties: ['Tampa Sweethearts Original', 'Ybor City Collection'],
    atmosphere: 'historic',
    priceRange: '$',
    coordinates: { lat: 27.9605, lng: -82.4358 },
    featured: true,
  },
  {
    id: 'cigar-city-brewing',
    name: 'Cigar City Brewing',
    address: '3924 W Spruce St, Tampa, FL 33607',
    neighborhood: 'West Tampa',
    phone: '(813) 348-6363',
    website: 'https://cigarcitybrewing.com',
    hours: 'Sun-Thu 11am-10pm, Fri-Sat 11am-11pm',
    description: 'Award-winning craft brewery celebrating Tampa\'s cigar heritage. Cigar-friendly patio and beer flights named after local cigar history.',
    amenities: ['Craft Beer', 'Food', 'Outdoor Patio', 'Tours', 'Gift Shop'],
    atmosphere: 'modern',
    priceRange: '$$',
    coordinates: { lat: 27.9537, lng: -82.5052 },
  },
  {
    id: 'arturo-fuente-lounge',
    name: 'Arturo Fuente Cigar Club',
    address: '2302 N Howard Ave, Tampa, FL 33607',
    neighborhood: 'West Tampa',
    description: 'Private club affiliated with the legendary Fuente family. Membership required. Premium lounge experience.',
    amenities: ['Members Only', 'Premium Selection', 'Private Events'],
    specialties: ['Arturo Fuente Full Line', 'Rare Opus X'],
    atmosphere: 'upscale',
    priceRange: '$$$',
  },
  {
    id: 'edward-james',
    name: 'Edward James Cigars',
    address: '2002 N Howard Ave, Tampa, FL 33607',
    neighborhood: 'West Tampa',
    phone: '(813) 250-0505',
    hours: 'Mon-Sat 10am-7pm',
    description: 'Boutique cigar shop with extensive selection and knowledgeable staff. Popular with locals.',
    amenities: ['Walk-in Humidor', 'Lounge Seating', 'Accessories'],
    atmosphere: 'neighborhood',
    priceRange: '$$',
  },
];

const YBOR_CITY_BRANDS: LocalBrand[] = [
  {
    id: 'arturo-fuente',
    name: 'Arturo Fuente',
    founded: '1912',
    story: 'The Fuente family\'s cigar legacy began in West Tampa in 1912 when Arturo Fuente Sr. rolled his first cigars. After fires and setbacks, the family persevered, eventually relocating production to the Dominican Republic while maintaining deep Tampa roots. Today, Arturo Fuente is considered one of the finest cigar makers in the world.',
    notableCigars: ['Opus X', 'Hemingway', 'Don Carlos', 'Chateau Fuente'],
    whereToFind: ['Arturo Fuente Cigar Club', 'King Corona', 'Most premium retailers'],
    website: 'https://arturofuente.com',
    featured: true,
  },
  {
    id: 'tabanero',
    name: 'Tabanero Cigars',
    founded: '2010',
    story: 'Modern revival of Ybor City cigar making. Tabanero rolls all cigars on-site using traditional Cuban techniques, keeping the craft alive in the historic cigar district. Their Red Eye blend has won multiple awards.',
    notableCigars: ['Red Eye', 'Havana', 'Ybor City Series'],
    whereToFind: ['Tabanero Cigars (7th Ave)'],
    website: 'https://tabanerocigars.com',
    featured: true,
  },
  {
    id: 'tampa-sweethearts',
    name: 'Tampa Sweethearts',
    founded: '1920',
    story: 'One of the last surviving original Ybor City cigar makers. The Lopez family has been hand-rolling cigars for over a century, using recipes passed down through generations. A living piece of Tampa cigar history.',
    notableCigars: ['Original Blend', 'Presidente', 'Ybor Gold'],
    whereToFind: ['Tampa Sweethearts Cigar Co.'],
    website: 'https://tampasweethearts.com',
    featured: true,
  },
  {
    id: 'jc-newman',
    name: 'J.C. Newman Cigar Company',
    founded: '1895',
    story: 'America\'s oldest family-owned premium cigar maker, now in its fourth generation. Their El Reloj factory in Ybor City is the last operating cigar factory in Tampa and is being developed into the Cigar Museum.',
    notableCigars: ['Diamond Crown', 'Brick House', 'Perla del Mar'],
    whereToFind: ['J.C. Newman Factory Store', 'Major retailers'],
    website: 'https://jcnewman.com',
    featured: true,
  },
];

const YBOR_CITY_EVENTS: CityEvent[] = [
  {
    id: 'ybor-cigar-fest',
    name: 'Ybor City Cigar Festival',
    date: 'First Saturday in December',
    venue: 'Centennial Park, Historic Ybor City',
    description: 'The world\'s largest cigar festival. Free admission, 150+ vendors, live music, and the celebration of Tampa\'s cigar heritage.',
    type: 'festival',
    link: 'https://yborcigarfestival.com',
  },
  {
    id: 'first-friday',
    name: 'First Friday Ybor',
    date: 'First Friday of each month',
    venue: '7th Avenue',
    description: 'Monthly street festival with live music, food vendors, and special events at local cigar shops.',
    type: 'recurring',
  },
  {
    id: 'cigar-heritage-trail',
    name: 'Cigar Heritage Trail Tours',
    venue: 'Ybor City Museum State Park',
    description: 'Walking tours exploring Ybor City\'s cigar history, visiting historic factories and learning about the lectors who read to workers.',
    type: 'recurring',
    link: 'https://ybormuseum.org',
  },
  {
    id: 'newman-factory-tours',
    name: 'J.C. Newman Factory Tours',
    venue: 'El Reloj Factory',
    description: 'Tour America\'s last working cigar factory in Tampa. See hand-rolling in action and learn about 125+ years of cigar making.',
    type: 'recurring',
    link: 'https://jcnewman.com/pages/factory-tour',
  },
];

const YBOR_CITY_HISTORY: HistoricalNote[] = [
  {
    id: 'founding',
    title: 'The Birth of Cigar City',
    content: 'In 1885, Vicente Martinez Ybor moved his cigar operations from Key West to Tampa, seeking to escape labor unrest and find better conditions. He purchased 40 acres of scrubland northeast of Tampa and built a planned community for cigar workers, complete with houses, shops, and mutual aid societies.',
    year: '1885',
    category: 'founding',
  },
  {
    id: 'cigar-capital',
    title: 'Cigar Capital of the World',
    content: 'By 1929, Tampa produced nearly 500 million cigars annually, earning the title "Cigar Capital of the World." Over 200 cigar factories employed more than 12,000 workers, making cigars the city\'s dominant industry.',
    year: '1929',
    category: 'industry',
  },
  {
    id: 'lector-tradition',
    title: 'El Lector: The Reader',
    content: 'Unique to Tampa\'s cigar factories was the tradition of el lector—a reader who sat on an elevated platform and read newspapers, novels, and political tracts to workers as they rolled cigars. Workers pooled money to pay lectors, who became cultural leaders in the community.',
    category: 'culture',
  },
  {
    id: 'mutual-aid',
    title: 'Mutual Aid Societies',
    content: 'Cigar workers formed ethnic mutual aid societies that provided healthcare, education, and cultural programs. The Centro Asturiano, Centro Español, and L\'Unione Italiana built grand clubhouses that still stand today, representing the community\'s prosperity.',
    category: 'culture',
  },
  {
    id: 'decline',
    title: 'Industry Decline',
    content: 'The Great Depression, machine-made cigarettes, and the 1962 Cuban embargo devastated Tampa\'s cigar industry. By the 1980s, most factories had closed. However, boutique cigar makers and cultural preservation efforts have kept the tradition alive.',
    year: '1930s-1980s',
    category: 'industry',
  },
  {
    id: 'revitalization',
    title: 'Renaissance and Revival',
    content: 'Today, Ybor City is a National Historic Landmark District. Hand-rolling operations like Tabanero and Tampa Sweethearts continue the craft, while the annual Ybor City Cigar Festival celebrates the neighborhood\'s heritage.',
    year: '1990s-Present',
    category: 'culture',
  },
];

export const YBOR_CITY: CityGuide = {
  id: 'ybor-city',
  name: 'Ybor City',
  tagline: 'The Historic Cigar Capital of the World',
  region: 'Tampa Bay',
  country: 'USA',

  overview: 'Ybor City is a National Historic Landmark District in Tampa, Florida, founded in 1885 as a planned community for cigar workers. At its peak, over 200 cigar factories produced nearly 500 million cigars annually. Today, the neighborhood preserves this heritage through working cigar factories, historic architecture, and the world\'s largest cigar festival.',

  cigarSignificance: 'Ybor City was once the "Cigar Capital of the World," producing more hand-rolled cigars than any other place on Earth. The neighborhood gave birth to legendary brands like Arturo Fuente and remains home to America\'s last operating cigar factory. Walking 7th Avenue today, you can still watch master rollers craft cigars by hand.',

  lounges: YBOR_CITY_LOUNGES,
  localBrands: YBOR_CITY_BRANDS,
  events: YBOR_CITY_EVENTS,
  history: YBOR_CITY_HISTORY,

  culturalTips: [
    'Start your visit at the Ybor City Museum State Park to understand the history before exploring.',
    'Walk 7th Avenue (La Septima) to see historic buildings and cigar shops.',
    'Visit Tabanero or Tampa Sweethearts to watch hand-rolling demonstrations.',
    'Try a Cuban sandwich—invented in Ybor City for cigar workers.',
    'The Columbia Restaurant (est. 1905) is the oldest Spanish restaurant in the US.',
    'Centro Ybor offers dining and entertainment in the heart of the district.',
  ],

  bestTimeToVisit: 'October through April offers pleasant weather. The Ybor City Cigar Festival (first Saturday in December) is the highlight of the cigar calendar.',

  gettingAround: 'Ybor City is very walkable. The TECO Line Streetcar connects Ybor to downtown Tampa. Parking is available in garages near Centro Ybor.',

  neighborhoodHighlights: [
    {
      name: '7th Avenue (La Septima)',
      description: 'The historic main street lined with cigar shops, restaurants, and nightlife.',
      vibe: 'Historic, vibrant, touristy',
    },
    {
      name: 'West Tampa',
      description: 'The other historic cigar district, home to Arturo Fuente\'s roots and Cigar City Brewing.',
      vibe: 'Local, authentic, off-the-beaten-path',
    },
  ],

  isLive: true,
};

// ============================================
// COMING SOON CITIES
// ============================================

export const MIAMI: CityGuide = {
  id: 'miami',
  name: 'Miami',
  tagline: 'Little Havana & Beyond',
  region: 'South Florida',
  country: 'USA',
  overview: 'Miami\'s cigar culture is centered in Little Havana, where Cuban exiles kept the tradition alive. Calle Ocho is lined with cigar shops and rolling tables.',
  cigarSignificance: 'Little Havana became a second home for Cuban cigar culture after 1959. Many cigar makers who fled Cuba established shops and brands in Miami.',
  lounges: [],
  localBrands: [],
  events: [],
  history: [],
  culturalTips: [],
  isLive: false,
  comingSoon: true,
};

export const NEW_ORLEANS: CityGuide = {
  id: 'new-orleans',
  name: 'New Orleans',
  tagline: 'Cigars, Jazz & Southern Charm',
  region: 'Louisiana',
  country: 'USA',
  overview: 'New Orleans has a rich cigar tradition intertwined with jazz and the city\'s famous nightlife. The PCA Trade Show comes here annually.',
  cigarSignificance: 'Historic port city with deep tobacco trade connections. Home to the annual PCA Trade Show, the cigar industry\'s largest event.',
  lounges: [],
  localBrands: [],
  events: [],
  history: [],
  culturalTips: [],
  isLive: false,
  comingSoon: true,
};

export const LAS_VEGAS: CityGuide = {
  id: 'las-vegas',
  name: 'Las Vegas',
  tagline: 'High Rollers & Premium Smokes',
  region: 'Nevada',
  country: 'USA',
  overview: 'Las Vegas offers world-class cigar lounges, casino smoking rooms, and hosts the Big Smoke event annually.',
  cigarSignificance: 'Casino culture and cigar culture go hand in hand. Las Vegas hosts Big Smoke, one of the premier cigar events in the country.',
  lounges: [],
  localBrands: [],
  events: [],
  history: [],
  culturalTips: [],
  isLive: false,
  comingSoon: true,
};

// All cities collection
export const CITIES: CityGuide[] = [
  YBOR_CITY,
  MIAMI,
  NEW_ORLEANS,
  LAS_VEGAS,
];

// Helper functions
export const getCityById = (id: string): CityGuide | undefined => {
  return CITIES.find(c => c.id === id);
};

export const getLiveCities = (): CityGuide[] => {
  return CITIES.filter(c => c.isLive);
};

export const getComingSoonCities = (): CityGuide[] => {
  return CITIES.filter(c => c.comingSoon);
};

export const getFeaturedLounges = (cityId: string): CityLounge[] => {
  const city = getCityById(cityId);
  return city?.lounges.filter(l => l.featured) || [];
};

export const getFeaturedBrands = (cityId: string): LocalBrand[] => {
  const city = getCityById(cityId);
  return city?.localBrands.filter(b => b.featured) || [];
};
